<template>
    <TransitionRoot
        as="template"
        :show="state.open"
    >
        <Dialog
            as="div"
            class="relative z-6000"
            :open="state.open === true"
            @close="dialogClose(state.overlayPreventClose)"
        >
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-black opacity-75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 overflow-y-auto">
                <div
                    class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
                >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 scale-0"
                        enter-to="opacity-100 translate-y-0 scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 scale-0"
                    >
                        <DialogPanel
                            class="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-7xl sm:p-6"
                        >
                            <div class="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                                <button
                                    type="button"
                                    class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-hidden"
                                    @click="dialogClose()"
                                >
                                    <span class="sr-only">Close</span>
                                    <MdiClose
                                        class="h-6 w-6"
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>
                            <div class="sm:flex sm:items-center sm:justify-start">
                                <div
                                    class="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gray-200 sm:mx-0 sm:h-10 sm:w-10"
                                >
                                    <MdiTableSync
                                        class="h-6 w-6 text-gray-600"
                                        aria-hidden="true"
                                    />
                                </div>
                                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                    <DialogTitle
                                        as="h3"
                                        class="text-base leading-6 font-semibold text-gray-900 select-none"
                                    >
                                        {{ t('buttonInsertAsTable') }}
                                    </DialogTitle>
                                </div>
                            </div>
                            <!-- Iterator builder sources -->
                            <div class="mt-2 flex w-full gap-2">
                                <nav
                                    class="h-96 max-h-96 w-72 max-w-72 min-w-72 rounded-md border-2 border-gray-300"
                                >
                                    <div
                                        class="border border-t-0 border-b-0 border-gray-300 px-3 py-2 text-sm shadow-sm"
                                    >
                                        <strong>{{
                                            placeholders[state.selectedPlaceholderGroup].name
                                        }}</strong>
                                        -
                                        {{
                                            placeholders[state.selectedPlaceholderGroup].description
                                        }}
                                    </div>
                                    <ul class="h-80 max-h-80 overflow-y-auto">
                                        <li
                                            v-for="variable in placeholders[
                                                state.selectedPlaceholderGroup
                                            ].model"
                                            :key="variable"
                                        >
                                            <a
                                                href="#"
                                                class="group block rounded-md p-2 pl-3 text-xs leading-6 font-semibold text-gray-700 hover:bg-gray-50 hover:text-blue-600"
                                                :title="
                                                    extractRealDescription(
                                                        placeholders[
                                                            state.selectedPlaceholderGroup
                                                        ],
                                                        variable,
                                                    )
                                                "
                                                @click="columnAdd(variable)"
                                            >
                                                {{ variable }}
                                            </a>
                                        </li>
                                    </ul>
                                </nav>

                                <!-- Iterator builder -->
                                <div class="h-96 max-h-96 flex-1 overflow-y-auto text-gray-600">
                                    <div class="flex items-start justify-start">
                                        <div
                                            v-for="(column, index) in iteratorTableColumns"
                                            :key="`${column.name}-${index}`"
                                            class="flex-column border border-gray-400 text-sm"
                                        >
                                            <div
                                                class="inline-flex w-full cursor-default justify-between gap-1 bg-gray-100 p-2 pr-0 font-medium select-none"
                                            >
                                                {{ column.name }}
                                                <ActionButton
                                                    v-tippy="t('buttonRemove')"
                                                    state="transparent"
                                                    :icon="MdiClose"
                                                    :is-disabled="false"
                                                    @click="columnRemove(index)"
                                                />
                                            </div>
                                            <div class="p-2">
                                                {{ column.key }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="mt-5 justify-end gap-2 sm:mt-4 sm:flex">
                                <ActionButton
                                    :value="t('buttonInsertAsTable')"
                                    state="primary"
                                    :icon="MdiTableSync"
                                    :is-disabled="iteratorTableColumns.length === 0"
                                    @click="iteratorInsert()"
                                />
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup>
    import { reactive } from 'vue';
    import ActionButton from '../ui/ActionButton.vue';
    import {
        Dialog,
        DialogPanel,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
    } from '@headlessui/vue';
    import { MdiTableSync, MdiClose } from 'materialdesignicons-vue3/icons/';
    import {
        createIteratorMarkup,
        extractRealDescription,
    } from '../composables/usePlaceholderTypes';

    defineExpose({
        name: 'IteratorEditor',
    });

    const props = defineProps({
        placeholders: {
            type: Object,
            required: true,
        },
        state: {
            type: Object,
            required: true,
        },
    });

    const placeholders = props.placeholders;
    const iteratorTableColumns = reactive([]);
    const state = props.state;

    const setColumn = (variable) => {
        return {
            key: variable,
            name: extractRealDescription(placeholders[state.selectedPlaceholderGroup], variable),
        };
    };

    const columnAdd = (variable) => {
        iteratorTableColumns.push(setColumn(variable));
    };

    const columnRemove = (index) => {
        iteratorTableColumns.splice(index, 1);
    };

    const iteratorInsert = () => {
        console.log(
            'WATCH ConditionEditorWidget.vue iteratorInsert str',
            placeholders[state.selectedPlaceholderGroup].name,
            iteratorTableColumns,
        );
        const str = createIteratorMarkup(
            placeholders[state.selectedPlaceholderGroup].name,
            iteratorTableColumns,
        );
        state.action(str);
        state.open = false;
    };

    const dialogClose = (preventClose = false) => {
        if (preventClose === false) {
            state.open = false;
        }
    };
</script>
